<template>
  <div>
    <app-header title="Bulk Import" subtitle="Templates">
      <b-button
        variant="primary"
        class="mr-1"
        :disabled="!templates.length || saving"
        @click="confirmSaveAll()"
      >
        <i class="fa fa-save" />
        Save All
      </b-button>
      <cancel-button />
    </app-header>
    <b-container>
      <b-spinner v-show="saving || loading" />
      <div v-if="saving">
        {{ successList.length }} /
        {{ templates.length + successList.length }} templates saved
      </div>
      <div v-if="!loading">
        <h4 v-if="successList.length">
          Successfully saved:
        </h4>
        <ul class="template-bulk-import__success-list">
          <li v-for="item in successList" :key="`success-template-${item.id}`">
            <a :href="`#/template/${item.id}`" target="_blank">
              {{ item.filename }}
            </a>
          </li>
        </ul>
        <div :class="{ 'template-bulk-import--disabled': saving }">
          <app-drag-drop-file-select
            @select="addFiles"
            multiple
            accept="image/svg+xml"
          />
          <br />
          <app-smart-import-table
            v-model="templates"
            @save-index="confirmSaveTemplateAt"
          />
        </div>
        <br />
        <app-smart-import-description />
      </div>
    </b-container>
  </div>
</template>
<script>
import $api from '@/services/api';
import $savefile from '@/services/savefile';

import { mapGetters } from 'vuex';

import AppHeader from '@/components/AppHeader';
import CancelButton from '@/components/CancelButton';
import AppDragDropFileSelect from '@/components/DragDropFileSelect';
import AppSmartImportTable from '@/components/templates/SmartImportTable';
import AppSmartImportDescription from '@/components/templates/SmartImportDescription';

const TEMPLATES_FOLDER = 'templates/svg';

export default {
  components: {
    AppHeader,
    CancelButton,
    AppDragDropFileSelect,
    AppSmartImportTable,
    AppSmartImportDescription,
  },
  computed: {
    ...mapGetters('smartimport', {
      loading: 'loading',
      deviceIdByFilename: 'deviceIdByFilename',
      templateTypeIdByFilename: 'templateTypeIdByFilename',
      templateTypeNameByFilename: 'templateTypeNameByFilename',
      categoriesByFilename: 'categoriesByFilename',
      optionsByFilename: 'optionsByFilename',
      getPossibleDuplicates: 'getPossibleDuplicates',
    }),
    matchDevice() {
      return (filename) => {
        return this.deviceIdByFilename(filename);
      };
    },
    matchTemplateTypeId() {
      return (filename) => {
        return this.templateTypeIdByFilename(filename);
      };
    },
    matchTemplateTypeName() {
      return (filename) => {
        return this.templateTypeNameByFilename(filename);
      };
    },
    matchCategories() {
      return (filename) => {
        return this.categoriesByFilename(filename);
      };
    },
    matchOptions() {
      return (filename) => {
        return this.optionsByFilename(filename);
      };
    },
    matchDuplicates() {
      return (filename) => {
        const duplicates = this.getPossibleDuplicates(filename);
        return duplicates;
      };
    },
  },
  data() {
    return {
      templates: [],
      saveError: null,
      successList: [],
      saving: false,
    };
  },
  methods: {
    confirmSaveAll() {
      const duplicatesMayExist = this.templates.filter(
        (template) => template.duplicate_warning.length,
      );
      const confirmMessage = duplicatesMayExist.length
        ? 'WARNING: Duplicates may exist. Are you sure you want to save all templates?'
        : 'Are you sure you want to save all templates?';
      if (confirm(confirmMessage)) {
        this.saveAll();
      }
    },
    async saveAll() {
      this.saveError = null;
      await this.saveTemplateAt(0);
      if (this.templates.length > 0 && !this.saveError) {
        this.saveAll();
      } else {
        this.$store.dispatch('templates/fetch');
      }
    },
    confirmSaveTemplateAt(index) {
      const template = this.templates[index];
      const confirmMessage = template.duplicate_warning.length
        ? `WARNING: Duplicates may exist. Are you sure you want to save ${template.filename}?`
        : `Are you sure you want to save ${template.filename}?`;
      if (confirm(confirmMessage)) {
        this.saveTemplateAt(index);
      }
    },
    async saveTemplateAt(index) {
      this.saving = true;
      const template = this.templates[index];
      try {
        const svg = await $savefile(template.file, TEMPLATES_FOLDER);
        if (!svg) {
          throw new Error('Failed to save SVG');
        }
        template.svg = svg;
        const response = await $api.post('/v1/internal/templates', template);
        if (response) {
          response.filename = template.filename;
          this.successList.push(response);
        } else {
          throw new Error('Failed to save template');
        }
        this.templates.splice(index, 1);
      } catch (e) {
        this.saveError = `Failed to save file ${template.filename}: ${e.message}`;
        alert(this.saveError);
      }
      this.saving = false;
    },
    addFiles(files) {
      const newTemplates = files.map((file) => {
        const template = {
          temp_id: `temp-file-${Math.random()
            .toString(36)
            .substring(7)}`,
          name: this.matchTemplateTypeName(file.name),
          type_id: this.matchTemplateTypeId(file.name),
          filename: file.name,
          device_id: this.matchDevice(file.name),
          file,
          ...this.matchCategories(file.name),
          ...this.matchOptions(file.name),
          duplicate_warning: this.matchDuplicates(file.name),
        };
        return template;
      });

      this.templates.push(...newTemplates);
    },
  },
};
</script>
<style scoped>
.template-bulk-import--disabled {
  pointer-events: none;
  opacity: 0.5;
}
</style>
